import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";
import {
    Container,
    Box,
    TextField,
    Button,
    Typography,
    Link,
    CircularProgress,
    Card,
    CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { StringsService } from "../services/strings/strings";
import AsyncString from "../shared/asyncString";
import "./auth.css";
import { Stack } from "@mui/system";

export const ForgotPasswordPage = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const navigate = useNavigate();

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage(null);
        setErrorMessage(null);

        try {
            await sendPasswordResetEmail(auth, email);
            setSuccessMessage(await StringsService.getStringById("success-password-reset-email"));
        } catch (error: any) {
            if (error.code === "auth/user-not-found") {
                setErrorMessage(await StringsService.getStringById("error-user-not-found"));
            } else if (error.code === "auth/invalid-email") {
                setErrorMessage(await StringsService.getStringById("error-email-invalid"));
            } else {
                setErrorMessage(await StringsService.getStringById("error-email-reset-generic"));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='main-container'>
            <Container
                maxWidth="xs"
                sx={{
                    display: "flex",
                    paddingLeft: 8,
                    paddingRight: 8,
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                }}
            >
                <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
                    <AsyncString stringId="forgot-password" />
                </Typography>
                <Typography
                    variant="body2"
                    marginBottom={2}
                    textAlign="center"
                    sx={{ color: "#6c757d" }}
                >
                    <AsyncString stringId="email-reset-description" />

                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: "100%",
                        background: "#fff",
                        borderRadius: 2,
                        padding: 3,
                        boxShadow: 3,
                    }}
                    component="form"
                    onSubmit={handleForgotPassword}>

                    <Box sx={{ width: '100%' }}>
                        <Stack
                            alignItems="end"
                            justifyContent="space-between"
                        >
                        <TextField
                            label={<AsyncString stringId="email-address" />}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            required
                            margin="normal"
                            variant="outlined"
                        />
                            <Link
                                href="/login"
                                underline="hover"
                                variant="body2"
                                color="primary"
                            >
                                <AsyncString stringId="back-to-login" />
                            </Link>
                        </Stack>
                    </Box>


                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{ background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                        disabled={loading}
                    >
                        {loading ?
                            <CircularProgress size={24} sx={{ color: "black" }} /> :
                            <AsyncString stringId="send-reset-email" />}
                    </Button>
                </Box>

                {successMessage && (
                    <Typography color="success.main" marginTop={2} textAlign="center">
                        {successMessage}
                    </Typography>
                )}

                {errorMessage && (
                    <Typography color="error.main" marginTop={2} textAlign="center">
                        {errorMessage}
                    </Typography>
                )}
            </Container>
        </div>
    );
};
